import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '@/i18n/react';
import { Button } from '@/components/ui';
import { useBackHandler } from '@/hooks/useBackHandler';
import { markOnboardingDone, type OnboardingState } from '@/lib/onboarding';
import { useOnboardingStore } from '@/store/onboardingStore';
import './tutorial.css';

/**
 * Tour steps — the list Geri approved (#97). Every target is on Home or the
 * dock, so the tour never navigates. A missing target (e.g. a hidden widget)
 * shows its text without a highlight instead of breaking the tour.
 */
export const TUTORIAL_STEPS = [
  { id: 'start', target: '[data-tour="start"]' },
  { id: 'summary', target: '[data-tour="summary"]' },
  { id: 'grund', target: '[data-tour="grund"]' },
  { id: 'community', target: '[data-tour="community"]' },
  { id: 'profile', target: '[data-tour="profile"]' },
  { id: 'header', target: '[data-tour="header"]' },
  { id: 'assistant', target: '.home__assistant' },
] as const;

/**
 * Whether to ask „Megnézed, mi hol van?" now: once the wizard is closed (or
 * was just closed in this session) and the tour was never answered.
 */
export function shouldAskTutorial(input: {
  uid: string | null;
  onboarding: OnboardingState;
  wizardClosed: boolean;
  answered: boolean;
}): boolean {
  const { uid, onboarding } = input;
  return (
    !!uid &&
    onboarding.loaded &&
    (onboarding.profileWizardAt !== null || input.wizardClosed) &&
    onboarding.tutorialAt === null &&
    !input.answered
  );
}

/** Another layer's dialog (not the tour's own) — the tour waits behind it. */
const OTHER_DIALOG = '[role="dialog"]:not(.tour):not(.tour-ask)';

/**
 * Whether some other dialog (tester mode chooser, privacy zone sheet, OTP…)
 * is open. The tour must not run over it: that layer would cover the very
 * buttons the coach marks point at. Watches the DOM only while `enabled`.
 */
function useOtherDialogOpen(enabled: boolean): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled || typeof MutationObserver === 'undefined') {
      setOpen(false);
      return undefined;
    }
    const check = () => setOpen(document.querySelector(OTHER_DIALOG) !== null);
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [enabled]);
  return open;
}

const HIGHLIGHT_PAD = 6;
const CARD_GAP = 12;

/**
 * Bemutató (Geri, #96; docs/02 → Bemutató): first the question „Megnézed, mi
 * hol van?", then coach marks over the live UI. Either answer closes it for
 * good (`private/settings.onboarding.tutorialAt`); Settings → Bemutató starts
 * it again. It comes after the profile wizard, in the same app start, on Home.
 */
export function TutorialLayer({ uid, onboarding }: { uid: string | null; onboarding: OnboardingState }) {
  const { pathname } = useLocation();
  const wizardClosed = useOnboardingStore((state) => state.wizardClosed);
  const answered = useOnboardingStore((state) => state.tutorialAnswered);
  const phase = useOnboardingStore((state) => state.tutorial);
  const answer = useOnboardingStore((state) => state.answerTutorial);
  const stop = useOnboardingStore((state) => state.stopTutorial);

  const asking = !!uid && shouldAskTutorial({ uid, onboarding, wizardClosed, answered });
  const wanted = pathname === '/' && (phase === 'running' || asking);
  const blocked = useOtherDialogOpen(wanted);

  if (!wanted || blocked) return null;
  if (phase === 'running') return <CoachMarks onDone={stop} />;
  if (!uid) return null;

  return (
    <TutorialQuestion
      onAnswer={(watch) => {
        answer(watch);
        void markOnboardingDone(uid, 'tutorialAt').catch(() => {});
      }}
    />
  );
}

function TutorialQuestion({ onAnswer }: { onAnswer: (watch: boolean) => void }) {
  const t = useT().tutorial;
  useBackHandler(true, () => onAnswer(false));
  return (
    <div className="tour-ask" role="dialog" aria-modal="true" aria-labelledby="tour-ask-title">
      <div className="tour-ask__sheet">
        <h2 id="tour-ask-title" className="tour-ask__title">{t.question}</h2>
        <p className="tour-ask__body">{t.questionBody}</p>
        <Button block onClick={() => onAnswer(true)}>
          {t.watch}
        </Button>
        <Button block variant="ghost" onClick={() => onAnswer(false)}>
          {t.decline}
        </Button>
      </div>
    </div>
  );
}

/** The live rectangle of the step's target, kept fresh on resize and scroll. */
function useTargetRect(selector: string): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    const element = document.querySelector(selector);
    if (!element) {
      setRect(null);
      return undefined;
    }
    element.scrollIntoView({ block: 'nearest' });
    const update = () => setRect(element.getBoundingClientRect());
    update();
    const frame = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [selector]);
  return rect;
}

export function CoachMarks({ onDone }: { onDone: () => void }) {
  const t = useT().tutorial;
  const [index, setIndex] = useState(0);
  const step = TUTORIAL_STEPS[index] ?? TUTORIAL_STEPS[0];
  const text = t.steps[step.id];
  const rect = useTargetRect(step.target);
  const last = index === TUTORIAL_STEPS.length - 1;
  useBackHandler(true, onDone);

  const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
  const below = rect ? rect.top + rect.height / 2 < viewportHeight / 2 : false;
  const cardStyle = rect
    ? below
      ? { top: rect.bottom + HIGHLIGHT_PAD + CARD_GAP }
      : { bottom: viewportHeight - rect.top + HIGHLIGHT_PAD + CARD_GAP }
    : { top: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-labelledby="tour-title">
      {rect ? (
        <div
          className="tour__spot"
          style={{
            top: rect.top - HIGHLIGHT_PAD,
            left: rect.left - HIGHLIGHT_PAD,
            width: rect.width + HIGHLIGHT_PAD * 2,
            height: rect.height + HIGHLIGHT_PAD * 2,
          }}
        />
      ) : (
        <div className="tour__scrim" />
      )}
      <div className="tour__card" style={cardStyle} key={step.id}>
        <div className="tour__count">{t.progress(index + 1, TUTORIAL_STEPS.length)}</div>
        <h2 id="tour-title" className="tour__title">{text.title}</h2>
        <p className="tour__body">{text.body}</p>
        <div className="tour__actions">
          <Button variant="ghost" size="sm" onClick={onDone}>
            {t.skip}
          </Button>
          <Button size="sm" onClick={() => (last ? onDone() : setIndex(index + 1))}>
            {last ? t.done : t.next}
          </Button>
        </div>
      </div>
    </div>
  );
}
